import type { OnDestroy } from '@angular/core';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

import { ButtonComponent } from '@app/ui/components/button/button.component';

import { type GlowSkin, EventButtonComponent, ZipPackAnimationComponent } from '../../../../shared/components';
import { AprilMadnessEventModalsService, AprilMadnessEventService, AprilMadnessSoundsService } from '../../../services';
import type { IEventLevel } from '../../../types/frontend';
import { getRarityGlowSkin } from '../../../types/frontend/skin-rarity.const';
import { HoloCardComponent } from '../../components/holo-card/holo-card.component';
import { APRIL_MADNESS_ZIP_PACK_BY_VARIANT, getAprilMadnessPackVariant } from '../../shared/april-madness-pack-variant.const';
import { type ICardStoryContent, APRIL_MADNESS_CARD_STORY_CONTENT } from './card-story-content.const';

export interface AprilMadnessOpenCaseModalPayload {
  step?: number;
  isReplay?: boolean;
  levelBackendId?: number;
  viewOnly?: boolean;
}

const TEAR_SOUNDS_COUNT = 9;
const TOTAL_STEPS = 31;
const STORY_CONTENT_REVEAL_DELAY_MS = 180;
const APRIL_MADNESS_ZIP_ANIMATION_PATH = '/assets/spine-animations/april-madness/open-case-pack/';
const APRIL_MADNESS_PACK_SKIN_PREFIXES = ['packets', ''] as const;

@Component({
  selector: 'app-april-madness-open-case-modal',
  templateUrl: './april-madness-open-case-modal.component.html',
  styleUrl: './april-madness-open-case-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, MatIcon, ZipPackAnimationComponent, HoloCardComponent, EventButtonComponent, TranslatePipe],
})
export class AprilMadnessOpenCaseModalComponent implements OnDestroy {
  readonly #aprilMadnessEventModalsService = inject(AprilMadnessEventModalsService);
  readonly #aprilMadnessEventService = inject(AprilMadnessEventService);
  readonly #aprilMadnessSoundsService = inject(AprilMadnessSoundsService);
  readonly #payload = inject<AprilMadnessOpenCaseModalPayload>(MAT_DIALOG_DATA);

  readonly zipPackAnimation = viewChild<ZipPackAnimationComponent>('zipPackAnimation');

  readonly step = computed<number>(() => this.#payload.step || 1);
  readonly isReplay = computed<boolean>(() => this.#payload.isReplay || false);
  readonly levelBackendId = computed<number | undefined>(() => this.#payload.levelBackendId);
  readonly viewOnly = computed<boolean>(() => this.#payload.viewOnly || false);
  readonly totalSteps = TOTAL_STEPS;
  readonly zipAnimationPath = APRIL_MADNESS_ZIP_ANIMATION_PATH;
  readonly zipPackSkinPrefixes = APRIL_MADNESS_PACK_SKIN_PREFIXES;

  readonly level = computed<IEventLevel | undefined>(() => {
    const eventLevels = this.#aprilMadnessEventService.eventLevels();
    return eventLevels.find((eventLevel) => eventLevel.levelId === this.step());
  });

  readonly cardStoryContent = computed<ICardStoryContent | null>(() => {
    return APRIL_MADNESS_CARD_STORY_CONTENT[this.step()] ?? null;
  });

  readonly glowSkin = computed<GlowSkin>(() => {
    return getRarityGlowSkin(this.level()?.item?.rarity);
  });

  readonly zipPackSkin = computed<string>(() => {
    const packVariant = getAprilMadnessPackVariant(this.level()?.caseType);
    return APRIL_MADNESS_ZIP_PACK_BY_VARIANT[packVariant];
  });

  readonly showZipPack = signal(true);
  readonly isPackLoaded = signal(false);
  readonly showCard = signal(false);
  readonly zipPackAnimatingOut = signal(false);
  readonly isCardFlipped = signal(false);
  readonly showStoryContent = signal(false);

  #currentTearSoundIndex = 0;
  #storyContentRevealTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    afterNextRender(() => {
      if (this.viewOnly()) {
        this.#initViewOnlyMode();
      } else {
        this.#aprilMadnessSoundsService.playShowPackSound();
      }
    });
  }

  ngOnDestroy(): void {
    this.#clearStoryContentRevealTimeout();
  }

  #initViewOnlyMode(): void {
    this.showZipPack.set(false);
    this.showCard.set(true);
    this.isCardFlipped.set(true);
    this.showStoryContent.set(!!this.cardStoryContent());
  }

  openPack(): void {
    this.#aprilMadnessSoundsService.playOpenButtonSound();
    this.#aprilMadnessSoundsService.playOpeningMusic();
    this.zipPackAnimation()?.playAutoOpenAnimation();
    this.#playAutoTearSounds();
  }

  // РќРµ РїСЂРёРґСѓРјР°Р» РЅРёС‡РµРіРѕ Р»СѓС‡С€Рµ. РўРѕР»СЊРєРѕ РµСЃР»Рё c rxjs
  #playAutoTearSounds(): void {
    const tearDelay = 80;
    for (let i = 0; i < TEAR_SOUNDS_COUNT; i++) {
      setTimeout(() => {
        this.#aprilMadnessSoundsService.playTearSound(i);
      }, i * tearDelay);
    }
    setTimeout(() => {
      this.#aprilMadnessSoundsService.playTearFinishSound();
    }, TEAR_SOUNDS_COUNT * tearDelay);
  }

  onHoldStarted(): void {
    this.#currentTearSoundIndex = 0;
    this.#aprilMadnessSoundsService.playOpeningMusic();
  }

  onTearProgress(progress: number): void {
    const soundThreshold = 100 / TEAR_SOUNDS_COUNT;
    const targetSoundIndex = Math.min(Math.floor(progress / soundThreshold), TEAR_SOUNDS_COUNT - 1);

    while (this.#currentTearSoundIndex <= targetSoundIndex) {
      this.#aprilMadnessSoundsService.playTearSound(this.#currentTearSoundIndex);
      this.#currentTearSoundIndex++;
    }
  }

  onTearFinished(): void {
    this.#aprilMadnessSoundsService.playTearFinishSound();
  }

  onPackOpened(): void {
    this.zipPackAnimatingOut.set(true);
    this.showZipPack.set(false);
    this.showCard.set(true);
    this.#scheduleStoryContentReveal();
  }

  onCardButtonClick(): void {
    if (this.viewOnly()) {
      this.close();
      return;
    }

    if (this.isCardFlipped()) {
      this.#aprilMadnessSoundsService.playToCollectionButtonSound();
      this.close();
    } else {
      this.isCardFlipped.set(true);
      this.#aprilMadnessSoundsService.playTurnCardSound();
    }
  }

  close(): void {
    this.#aprilMadnessEventModalsService.closeOpenCaseModal();
  }

  onPackLoaded(): void {
    this.isPackLoaded.set(true);
  }

  #scheduleStoryContentReveal(): void {
    this.#clearStoryContentRevealTimeout();

    if (!this.cardStoryContent()) {
      this.showStoryContent.set(false);
      return;
    }

    this.showStoryContent.set(false);
    this.#storyContentRevealTimeoutId = setTimeout(() => {
      this.showStoryContent.set(true);
      this.#storyContentRevealTimeoutId = null;
    }, STORY_CONTENT_REVEAL_DELAY_MS);
  }

  #clearStoryContentRevealTimeout(): void {
    if (!this.#storyContentRevealTimeoutId) {
      return;
    }

    clearTimeout(this.#storyContentRevealTimeoutId);
    this.#storyContentRevealTimeoutId = null;
  }
}
